import type { MaybeRefOrGetter, Ref } from 'vue'
import { refDebounced } from '@vueuse/core'
import { computed, ref, toValue, watch } from 'vue'

export interface UseAsyncComboboxFilterOptions {
  minLength?: MaybeRefOrGetter<number>
  debounceMs?: MaybeRefOrGetter<number>
}

export function useAsyncComboboxFilter<T>(
  fetcher: (query: string) => Promise<T[]>,
  options: UseAsyncComboboxFilterOptions = {},
) {
  const open = ref(false)
  const query = ref('')
  const minLength = computed(() => toValue(options.minLength) ?? 2)
  const debounceMs = computed(() => toValue(options.debounceMs) ?? 250)
  const debouncedQuery = refDebounced(query, debounceMs)
  const filtered = ref<T[]>([]) as Ref<T[]>
  const loading = ref(false)

  let requestId = 0

  watch(debouncedQuery, async (q) => {
    const id = ++requestId
    if (q.length < minLength.value) {
      filtered.value = []
      loading.value = false
      return
    }
    loading.value = true
    try {
      const result = await fetcher(q)
      if (id === requestId) {
        filtered.value = result
      }
    }
    catch {
      if (id === requestId) {
        filtered.value = []
      }
    }
    finally {
      if (id === requestId) {
        loading.value = false
      }
    }
  })

  function reset() {
    open.value = false
    query.value = ''
    filtered.value = []
    requestId++
  }

  return { open, query, debouncedQuery, filtered, loading, reset }
}
